import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FileUtils } from 'src/app/@core/utils/file-utils';
import { ObjectUtils } from 'src/app/@core/utils/object-utils';
import { PosValidators } from 'src/app/@core/validators/pos-validators';
import { ColumnTable } from 'src/app/models/column-table';
import { CreditRequest } from 'src/app/models/credit-request';
import { CreditRequestRequirement } from 'src/app/models/credit-request-requirements';
import { OnbaseDoc } from 'src/app/models/onbase-doc';
import { Paging } from 'src/app/models/page';
import { OnbaseProcedureResponseModel } from 'src/app/models/response/onbaseProcedureResponseModel';
import { PosDocumentsStepModalService } from './../../@core/services/pos-documents-step-modal.service';
import { AttachedDocumentTableModel } from './../../models/attached-document-table-model';

@Component({
  selector: 'app-pos-credit-request-requirement-detail',
  templateUrl: './pos-credit-request-requirement-detail.component.html',
  styleUrls: ['./pos-credit-request-requirement-detail.component.sass'],
})
export class PosCreditRequestRequirementDetailComponent implements OnInit {
  @Input() requirement: CreditRequestRequirement =
    new CreditRequestRequirement();

  @Input() creditPlan: OnbaseProcedureResponseModel =
    new OnbaseProcedureResponseModel();

  @Input() onbaseDocs: Array<OnbaseDoc> = new Array();

  @Input() creditRequest: CreditRequest = new CreditRequest();

  @Output() requirementConfirmed: EventEmitter<{
    comment: string;
    files: Array<AttachedDocumentTableModel>;
  }> = new EventEmitter();

  documentsForm: FormArray = new FormArray([]);
  commentsForm: FormGroup = new FormGroup({});

  documents: Paging<Array<AttachedDocumentTableModel>> = new Paging([]);

  parsedCreateDateAndTime: string = '';
  parsedEndDateAndTime: string = '';

  constructor(
    public bsModalRef: BsModalRef,
    private readonly posDocumentsStepModalService: PosDocumentsStepModalService,
    private readonly datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.configureDocumentsForm();
    this.configureCommentsForm();
    this.parsedCreateDateAndTime =
      this.datePipe.transform(
        this.requirement.createdTimezoneDate,
        `dd-MM-yyyy hh:mm:ss a`
      ) || '';
    if (
      this.requirement.completedBy !== '' &&
      this.requirement.completedBy != undefined
    ) {
      this.parsedEndDateAndTime =
        this.datePipe.transform(
          this.requirement.confirmationDateTimezoneDate,
          `dd-MM-yyyy hh:mm:ss a`
        ) || '';
    }
  }

  private configureCommentsForm() {
    const commentsFormControl = new FormControl(this.requirement.notes);

    if (this.requirement.notes && this.requirement.notes.length > 0) {
      commentsFormControl.disable();
    }

    this.commentsForm = new FormGroup({
      comments: commentsFormControl,
    });
  }

  close() {
    this.bsModalRef.hide();
  }

  configureDocumentsForm() {
    this.documentsForm = new FormArray([]);
    ObjectUtils.objectToArray(this.creditPlan.documents || {}).forEach((m) => {
      let arrayOfDocuments = m.value as Array<string>;

      let documents = new FormArray([]);

      arrayOfDocuments.forEach((x, i) => {
        documents.push(
          new FormGroup({
            [`description_${m.key}_${i.toString()}`]: new FormControl(x),
            [`document_fileName_${m.key}_${i.toString()}`]: new FormControl(''),
            [`document_${m.key}_${i.toString()}`]: new FormControl(''),
            [`document_ext_${m.key}_${i.toString()}`]: new FormControl(''),
            [`document_documentType_${m.key}_${i.toString()}`]: new FormControl(
              ''
            ),
          })
        );
      });

      let formToPush = new FormGroup({
        id: new FormControl(m.key),
        description: new FormControl(
          this.onbaseDocs.find((x) => x.code == m.key)?.name
        ),
        documents: documents,
      });
      this.documentsForm.push(formToPush);
    });
    this.documentsForm.setValidators(PosValidators.validateDocuments);
  }

  async showDocuments(event: Event): Promise<void> {
    event.stopPropagation();
    const modalDialogResponse = await this.posDocumentsStepModalService.show(
      this.documentsForm
    );

    const documents = (await this.parseDocuments(modalDialogResponse))
      .flatMap((f) => f)
      .filter((f) => f) as Array<AttachedDocumentTableModel>;

    console.log(documents);

    this.documents = new Paging(documents);
  }

  private async parseDocuments(modalDialogResponse: any) {
    return await Promise.all(
      modalDialogResponse.documentsFormValue.map(async (m: any) => {
        let docArray = m.documents as Array<any>;
        let result = await Promise.all(
          docArray.map(async (x: any, i: number) => {
            let file = x[`document_${m.id}_${i}`];
            if (file) {
              return {
                documentType: m.id,
                fileType: file.type,
                requestNumber: this.creditRequest.code,
                identificationDocument:
                  this.creditRequest.customer.mainPersonalInfo.fiscalId,
                file: await FileUtils.getBase64(file),
                category: m.description,
                documentList: x[`description_${m.id}_${i}`],
                extentionFile: file.name.split('.').pop(),
                id: `${(i + 1).toString()}`,
                toView: true,
                fileName: x[`document_fileName_${m.id}_${i}`],
              };
            }
            return;
          })
        );

        return result;
      })
    );
  }

  getAttachedDocumentTableHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable('id', 'N°'),
      new ColumnTable('category', 'Categoria'),
      new ColumnTable('documentList', 'Documento'),
      new ColumnTable('fileName', 'Archivo'),
      new ColumnTable('', '	Opciones', {
        type: 'action',
        iconClass: 'fa fa-cogs',
        isIcon: true,
        actions: [
          {
            fn: (file: AttachedDocumentTableModel) => console.log(file),
            icon: 'fa fa-eye',
            tooltip: 'Ver Requerimiento',
            isDisabled: false,
          },
        ],
      }),
    ];
  }

  completeRequirement(): void {
    const valueToEmit = {
      comment: this.commentsForm.value.comments,
      files: this.documents.content,
    };

    console.log(valueToEmit);

    this.requirementConfirmed.emit(valueToEmit);
    this.requirementConfirmed.complete();
    this.close();
  }
}
